import { useEffect, useMemo, useState } from 'react'
import type { ReactElement } from 'react'
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  Stack,
  Chip,
} from '@mui/material'
import SchoolIcon from '@mui/icons-material/School'
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import QuizIcon from '@mui/icons-material/Quiz'
import PercentIcon from '@mui/icons-material/Percent'
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined'
import type { SvgIconComponent } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../hooks/useAuth'
import { useRecentSessions } from '../hooks/useRecentSessions'
import { memorizationApi } from '../lib/api'
import { xpToLevel, xpForLevel } from '../lib/learning'
import { LoadingSpinner } from '../components/LoadingSpinner'

export default function LearnStats() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { sessions } = useRecentSessions(25)
  const [stats, setStats] = useState<Record<string, unknown> | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    memorizationApi
      .getStats()
      .then((data) => {
        if (!cancelled) setStats(data)
      })
      .catch((err) => {
        const code = (err as { code?: string } | null | undefined)?.code
        if (code !== 'AUTH_EXPIRED' && import.meta.env.DEV) {
          console.error('Error loading stats:', err)
        }
        if (!cancelled) setStats(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const startOfWeek = Date.now() - 7 * 24 * 60 * 60 * 1000
  const thisWeekGames = sessions.filter((s) => new Date(s.playedAt).getTime() >= startOfWeek).length

  if (loading) {
    return <LoadingSpinner message={t('memorization.loading')} />
  }

  return (
    <StatsView
      stats={stats}
      learningXp={user?.learningXp ?? 0}
      thisWeekGames={thisWeekGames}
      currentStreak={user?.currentStreak ?? 0}
      longestStreak={user?.longestStreak ?? 0}
    />
  )
}

interface NormalizedStats {
  totalVerses: number
  learning: number
  reviewing: number
  mastered: number
  totalTests: number
  averageScore: number
  lastTestScore: number | null
}

const KNOWN_FIELDS: Record<keyof NormalizedStats, string[]> = {
  totalVerses: ['totalVerses', 'total_verses'],
  learning: ['learning'],
  reviewing: ['reviewing'],
  mastered: ['mastered'],
  totalTests: ['totalTests', 'total_tests', 'testsCompleted', 'tests_completed'],
  averageScore: ['averageScore', 'average_score'],
  lastTestScore: ['lastTestScore', 'last_test_score'],
}

function pickNumber(stats: Record<string, unknown>, keys: string[]): number {
  for (const k of keys) {
    const v = stats[k]
    if (typeof v === 'number' && Number.isFinite(v)) return v
    if (typeof v === 'string') {
      const parsed = Number(v)
      if (Number.isFinite(parsed)) return parsed
    }
  }
  return 0
}

function pickOptionalNumber(
  stats: Record<string, unknown>,
  keys: string[],
): number | null {
  for (const k of keys) {
    const v = stats[k]
    if (typeof v === 'number' && Number.isFinite(v)) return v
    if (typeof v === 'string' && v !== '') {
      const parsed = Number(v)
      if (Number.isFinite(parsed)) return parsed
    }
  }
  return null
}

function normalizeStats(stats: Record<string, unknown>): NormalizedStats {
  return {
    totalVerses: pickNumber(stats, KNOWN_FIELDS.totalVerses),
    learning: pickNumber(stats, KNOWN_FIELDS.learning),
    reviewing: pickNumber(stats, KNOWN_FIELDS.reviewing),
    mastered: pickNumber(stats, KNOWN_FIELDS.mastered),
    totalTests: pickNumber(stats, KNOWN_FIELDS.totalTests),
    averageScore: pickNumber(stats, KNOWN_FIELDS.averageScore),
    lastTestScore: pickOptionalNumber(stats, KNOWN_FIELDS.lastTestScore),
  }
}

interface StatsViewProps {
  stats: Record<string, unknown> | null
  learningXp: number
  thisWeekGames: number
  currentStreak: number
  longestStreak: number
}

function StatsView({
  stats,
  learningXp,
  thisWeekGames,
  currentStreak,
  longestStreak,
}: StatsViewProps) {
  const { t, i18n } = useTranslation()

  useEffect(() => {
    if (!stats) return
    const known = new Set(Object.values(KNOWN_FIELDS).flat())
    const unknown = Object.keys(stats).filter((k) => !known.has(k))
    if (unknown.length > 0 && import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[LearnStats] Ignoring unknown stat fields:', unknown)
    }
  }, [stats])

  const normalized = useMemo(() => (stats ? normalizeStats(stats) : null), [stats])
  const level = xpToLevel(learningXp)
  const xpInLevel = learningXp - xpForLevel(level)
  const xpForNext = xpForLevel(level + 1) - xpForLevel(level)

  const totalVerses = normalized?.totalVerses ?? 0
  const learning = normalized?.learning ?? 0
  const reviewing = normalized?.reviewing ?? 0
  const mastered = normalized?.mastered ?? 0
  const totalTests = normalized?.totalTests ?? 0
  const averageScore = normalized?.averageScore ?? 0
  const lastTestScore = normalized?.lastTestScore ?? null
  const masteryPct = totalVerses > 0 ? Math.round((mastered / totalVerses) * 100) : 0

  return (
    <Grid container spacing={2}>
      {!normalized && (
        <Grid size={{ xs: 12 }}>
          <Typography color="text.secondary">{t('memorization.noStats')}</Typography>
        </Grid>
      )}

      {normalized && (
      <>
      <Grid size={{ xs: 12, md: 7 }}>
        <Card sx={{ height: '100%' }}>
          <CardContent
            sx={{
              p: 2,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              '&:last-child': { pb: 2 },
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              sx={{ alignItems: 'center', width: '100%' }}
            >
              <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={96}
                  thickness={4.5}
                  sx={{ color: 'grey.200' }}
                />
                <CircularProgress
                  variant="determinate"
                  value={masteryPct}
                  size={96}
                  thickness={4.5}
                  color="primary"
                  sx={{ position: 'absolute', left: 0 }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
                    {mastered}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    / {totalVerses}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                  {t('memorization.stats.heroOverline')}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
                  {t('memorization.stats.heroHeadline', { count: mastered })}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {t('memorization.stats.heroSubline', {
                    total: totalVerses,
                    pct: masteryPct,
                  })}
                </Typography>
                {lastTestScore !== null && (
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<EmojiEventsIcon />}
                    label={t('memorization.stats.lastTestPill', {
                      score: Math.round(lastTestScore),
                    })}
                    sx={{
                      mt: 1,
                      color: 'primary.main',
                      borderColor: 'primary.main',
                      '& .MuiChip-icon': { color: 'primary.main' },
                    }}
                  />
                )}
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, md: 5 }}>
        <Card sx={{ height: '100%' }}>
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Stack spacing={0} divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
              <SummaryStat
                icon={MenuBookIcon}
                value={totalVerses.toLocaleString(i18n.language)}
                label={t('memorization.stats.totalVerses')}
              />
              <SummaryStat
                icon={QuizIcon}
                value={totalTests.toLocaleString(i18n.language)}
                label={t('memorization.stats.totalTests')}
              />
              <SummaryStat
                icon={PercentIcon}
                value={`${Math.round(averageScore)}%`}
                label={t('memorization.stats.avgScore')}
              />
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      </>
      )}

      <Grid size={{ xs: 12 }}>
        <Card>
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Grid container>
              <Grid size={{ xs: 12, sm: 4 }}>
                <SummaryStat
                  icon={EmojiEventsOutlinedIcon}
                  value={`Lv ${level}`}
                  label={t('games.statLevel')}
                  sublabel={t('games.statLevelXp', { xpInLevel, xpForNext })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <SummaryStat
                  icon={CalendarTodayOutlinedIcon}
                  value={String(thisWeekGames)}
                  label={t('games.statThisWeek')}
                  sublabel={t('games.statGamesPlayed')}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <SummaryStat
                  icon={LocalFireDepartmentOutlinedIcon}
                  value={`${currentStreak}d`}
                  label={t('games.statStreak')}
                  sublabel={t('games.statStreakBest', { count: longestStreak })}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {normalized && (
      <Grid size={{ xs: 12 }}>
        <Card>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.25 }}>
              {t('memorization.stats.breakdownTitle')}
            </Typography>
            <BreakdownBar
              learning={learning}
              reviewing={reviewing}
              mastered={mastered}
            />
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 1.25, flexWrap: 'wrap', gap: 1 }}
            >
              <StatusChip
                icon={<SchoolIcon />}
                label={t('memorization.stats.learningCount', { count: learning })}
                color="warning"
              />
              <StatusChip
                icon={<LocalLibraryIcon />}
                label={t('memorization.stats.reviewingCount', { count: reviewing })}
                color="info"
              />
              <StatusChip
                icon={<EmojiEventsIcon />}
                label={t('memorization.stats.masteredCount', { count: mastered })}
                color="success"
              />
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      )}
    </Grid>
  )
}

interface SummaryStatProps {
  icon: SvgIconComponent
  value: string
  label: string
  sublabel?: string
}

function SummaryStat({ icon: Icon, value, label, sublabel }: SummaryStatProps) {
  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', py: 1.25, px: 0.5 }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: (theme) =>
            theme.palette.mode === 'dark'
              ? 'rgba(45,212,191,0.12)'
              : 'rgba(15,118,110,0.10)',
          color: 'primary.main',
          flexShrink: 0,
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        {sublabel && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {sublabel}
          </Typography>
        )}
      </Box>
    </Stack>
  )
}

function BreakdownBar({
  learning,
  reviewing,
  mastered,
}: {
  learning: number
  reviewing: number
  mastered: number
}) {
  const total = learning + reviewing + mastered
  if (total === 0) {
    return null
  }
  const lp = (learning / total) * 100
  const rp = (reviewing / total) * 100
  const mp = (mastered / total) * 100
  return (
    <Box
      sx={{
        display: 'flex',
        height: 8,
        borderRadius: 4,
        overflow: 'hidden',
        bgcolor: 'grey.200',
      }}
      role="img"
      aria-label="Status breakdown"
    >
      <Box sx={{ width: `${lp}%`, bgcolor: 'warning.main' }} />
      <Box sx={{ width: `${rp}%`, bgcolor: 'info.main' }} />
      <Box sx={{ width: `${mp}%`, bgcolor: 'success.main' }} />
    </Box>
  )
}

interface StatusChipProps {
  icon: ReactElement
  label: string
  color: 'warning' | 'info' | 'success'
}

function StatusChip({ icon, label, color }: StatusChipProps) {
  return <Chip icon={icon} label={label} color={color} variant="outlined" size="medium" />
}

